import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { CheckCircle2 } from "lucide-react";
import { useTranslation as useTranslationOrig } from "react-i18next";

// i18n safe (évite TS2589)
type RawT = (key: string, options?: Record<string, unknown>) => any;
const useTranslationSafe = (ns?: string) =>
  (useTranslationOrig as unknown as (ns?: any) => { t: RawT })(ns);

const Success = () => {
  const [checking, setChecking] = useState(true);
  const [premium, setPremium] = useState<boolean | null>(null);

  // i18n
  const { t: tRaw } = useTranslationSafe("success");
  const tr = useCallback(
    (key: string, options?: Record<string, unknown>): string => {
      const out = tRaw(key, options);
      if (typeof out === "string") return out;
      if (Array.isArray(out)) return out.join("");
      return String(out ?? "");
    },
    [tRaw]
  );

  useEffect(() => {
    (async () => {
      // petit délai pour laisser le temps au webhook Stripe de setter is_premium
      await new Promise((r) => setTimeout(r, 1500));

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { setChecking(false); return; }

      const { data, error } = await supabase
        .from("user_profiles")
        .select("is_premium")
        .eq("id", user.id)
        .maybeSingle();

      if (!error && data) setPremium(!!data.is_premium);
      setChecking(false);
    })();
  }, []);

  return (
    <section className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950">
      <div className="mx-auto w-full max-w-md rounded-2xl border border-gray-200 bg-white/80 p-8 shadow-sm dark:border-white/10 dark:bg-white/5">
        <div className="flex items-center justify-center gap-2">
          <CheckCircle2 className="h-6 w-6 text-green-600" />
          <h1 className="text-xl font-semibold">
            {tr("title", { defaultValue: "Paiement confirmé" })}
          </h1>
        </div>

        <p className="mt-3 text-sm text-gray-600 dark:text-gray-300">
          {tr("subtitle", {
            defaultValue: "Merci ! Ton paiement a bien été traité. On vérifie l’activation de ton compte Premium."
          })}
        </p>

        <div className="mt-6 text-sm">
          {checking && <p>{tr("checking", { defaultValue: "Vérification en cours…" })}</p>}

          {!checking && premium === true && (
            <p className="text-green-700 dark:text-green-400">
              {tr("active", { defaultValue: "Ton statut Premium est actif 🎉" })}
            </p>
          )}

          {!checking && premium === false && (
            <p className="text-amber-700 dark:text-amber-400">
              {tr("pending", {
                defaultValue: "Premium pas encore actif — le webhook peut prendre quelques secondes."
              })}
            </p>
          )}
        </div>

        <div className="mt-6 flex gap-2">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-lg bg-orange-600 px-4 py-2.5 font-semibold text-white hover:bg-orange-700"
          >
            {tr("cta.home", { defaultValue: "Revenir à l’accueil" })}
          </Link>
          <Link
            to="/pricing"
            className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white/70 px-4 py-2.5 font-semibold text-gray-900 hover:bg-white dark:border-white/10 dark:bg-white/5 dark:text-gray-100"
          >
            {tr("cta.features", { defaultValue: "Voir les fonctionnalités" })}
          </Link>
        </div>
      </div>
    </section>
  );
};

export default Success;
export const __SUCCESS_MODULE = true;
export {};
